import { Error, LoadingIndicator } from '@/components/shared/parts';
import { Chat, Order, ResponseChat } from '@/types';
import { isEmpty } from '@/utils';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChatItem } from './chatItem';
import { useOrderChat } from '../hooks';
import { ChatForm } from './chatForm';


const FETCH_CHATS_FAILED_MESSAGE =
  'チャットの取得に失敗しました。ページを再読み込みしてお試しください。';

const FETCH_OLDER_CHATS_FAILED_MESSAGE =
  '過去のメッセージの読み込みに失敗しました。時間をおいて再度お試しください。';

const getOldestChatId = (chats: ResponseChat): number | undefined => {
  const allChats = Object.values(chats).flat();
  if (allChats.length === 0) return undefined;
  return Math.min(...allChats.map((c) => c.id));
};

// 古いデータを前に、同日付は結合してマージ
const mergeChats = (older: ResponseChat, newer: ResponseChat): ResponseChat => {
  const merged: ResponseChat = { ...older };
  Object.entries(newer).forEach(([date, items]) => {
    merged[date] = [...(merged[date] ?? []), ...items];
  });
  return merged;
};

export const ChatList: FC<{
  order: Order;
}> = ({ order }) => {
  const [chats, setChats] = useState<ResponseChat>({});
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [errors, setErrors] = useState<Array<string>>([]);
  const { fetchChats } = useOrderChat();
  const chatBodyRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (): void => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  };

  const handleLoadChats = useCallback(async (): Promise<void> => {
    setErrors([]);
    try {
      const result = await fetchChats(order.id);
      setChats(result.data);
      setHasMore(result.hasMore);
    } catch (e) {
      console.error(FETCH_CHATS_FAILED_MESSAGE, e);
      setErrors([FETCH_CHATS_FAILED_MESSAGE]);
    }
  }, [fetchChats, order.id]);

  const handleLoadMore = async (): Promise<void> => {
    const oldestId = getOldestChatId(chats);
    if (oldestId === undefined) return;
    setErrors([]);
    setIsLoadingMore(true);
    try {
      const result = await fetchChats(order.id, oldestId);
      setChats((prev) => mergeChats(result.data, prev));
      setHasMore(result.hasMore);
    } catch (e) {
      console.error(FETCH_OLDER_CHATS_FAILED_MESSAGE, e);
      setErrors([FETCH_OLDER_CHATS_FAILED_MESSAGE]);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // 送信後は flushSync でDOM更新を同期的に完了させてからスクロール。画像ロード完了時にも再スクロール
  const handleSent = (chat: Chat): void => {
    const date = chat.chatDate;
    flushSync(() => {
      setChats((prev) => ({
        ...prev,
        [date]: [...(prev[date] ?? []), chat],
      }));
    });
    scrollToBottom();
    const images = chatBodyRef.current?.querySelectorAll('img') ?? [];
    Array.from(images)
      .filter((img) => !img.complete)
      .forEach((img) => {
        img.addEventListener('load', () => scrollToBottom(), { once: true });
        img.addEventListener('error', () => scrollToBottom(), { once: true });
      });
  };

  // 初回ロード完了後に最新メッセージまでスクロール
  useEffect(() => {
    if (!isInitialLoading) scrollToBottom();
  }, [isInitialLoading]);

  useEffect(() => {
    if (order === undefined) return;
    (async () => {
      await handleLoadChats();
      setIsInitialLoading(false);
    })();
  }, [order, handleLoadChats]);

  return (
    <div className='card chat-container'>
      <div className='card-header bg-white'>
        {order?.recreation?.isUserDeleted ? '削除済みユーザー' : order?.recreation?.profile?.name + 'さん'} とのチャット
      </div>
      <div className='card-body bg-ba02' ref={chatBodyRef}>
        {isInitialLoading ? (
          <div className='d-flex justify-content-center py-4'>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            {!isEmpty(errors) && <Error errors={errors} />}
            {hasMore && (
              <div className='d-flex justify-content-center mb-3'>
                {isLoadingMore ? (
                  <LoadingIndicator />
                ) : (
                  <button
                    type='button'
                    className='btn btn-outline-secondary btn-sm'
                    onClick={handleLoadMore}
                  >
                    過去のメッセージを読み込む
                  </button>
                )}
              </div>
            )}
            {Object.entries(chats)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, chatItems]) => (
                <ChatItem
                  key={date}
                  recreation={order?.recreation}
                  date={date}
                  chats={[...chatItems].sort((x, y) => x.id - y.id)}
                />
              ))}
          </>
        )}
      </div>
      <ChatForm
        order={order}
        onSent={handleSent}
        isSubmitDisabled={isLoadingMore || isInitialLoading || !isEmpty(errors)}
      />
    </div>
  );
};
